/**
 * Os rótulos do legado que as abas da frente A3 consultam e que nenhuma outra frente publicou.
 *
 * SÓ AS CHAVES QUE ALGUMA ABA DA A3 LÊ. O mapa inteiro de `PARAM_ABAS_LABEL` tem 16 chaves
 * (`admin.html:8787-8804`), mas a Leitura Estratégica e o Dossiê só consultam as sete de
 * `ABAS_CONTEUDO_ANALISE`: portar as outras nove seria código que nenhuma tela chama. Quando a
 * aba Sistema publicar o mapa inteiro, este arquivo passa a importar de lá.
 */

/**
 * As abas que viram conteúdo na análise por IA e no dossiê, na ordem do legado
 * (`ABAS_CONTEUDO_ANALISE`, `admin.html:6180`). As outras (Arquivos, Mensagens, Dossiê) são
 * navegação e entrega, e não dados da cliente, como explica o comentário de `6177-6179`.
 */
export const ABAS_CONTEUDO_ANALISE: readonly string[] = Object.freeze([
  'questionario',
  'personalidade',
  'radar_estilo_raiz',
  'questionario_avaliativo',
  'checklist_atendimento',
  'checklist_milhoes',
  'coloracao',
]);

/**
 * O rótulo de cada aba de conteúdo, palavra por palavra de `PARAM_ABAS_LABEL`
 * (`admin.html:8787-8804`), só nas sete chaves acima.
 */
export const PARAM_ABAS_LABEL: Readonly<Record<string, string>> = Object.freeze({
  checklist_atendimento: 'Checklist - Atendimento',
  checklist_milhoes: 'Checklist - Milhões',
  coloracao: 'Coloração - Ficha Atendimento',
  personalidade: 'Personalidade',
  questionario_avaliativo: 'Questionário Avaliativo',
  questionario: 'Questionário Geral',
  radar_estilo_raiz: 'Radar de Estilo Raiz',
});

/**
 * O nome do produto na chave de parâmetro, palavra por palavra de `PARAM_PRODUTOS_LABEL`
 * (`admin.html:8776-8785`). A aba E-mail Atendimento mostra este nome antes do arquivo.
 */
export const PARAM_PRODUTOS_LABEL: Readonly<Record<string, string>> = Object.freeze({
  acompanhamento_anual: 'Acompanhamento Anual',
  analise_proporcao_corporal_3d: 'Análise de Proporção Corporal 3D',
  coloracao_pessoal: 'Coloração Pessoal',
  estrategia_imagem_pessoal_profissional: 'Estratégia de Imagem Pessoal e Profissional (EIP)',
  mala_de_viagem: 'Mala de Viagem',
  montagem_de_looks: 'Montagem de Looks',
  personal_shopper: 'Personal Shopper',
  projetos_sob_medida: 'Projetos Sob Medida',
});

/**
 * A frase "Considerando: ..." das abas de IA e Dossiê (`admin.html:5564-5567` e `6185-6188`): as
 * abas de conteúdo habilitadas, na ordem de `ABAS_CONTEUDO_ANALISE`, com o rótulo de cada uma.
 * Sem nenhuma, devolve `null`, e cada aba escreve a própria frase de vazio, que é diferente.
 */
export function textoAbasConsideradas(abasHabilitadas: ReadonlySet<string>): string | null {
  // O filtro mantém a ordem da lista fixa, e não a ordem em que o banco devolveu.
  const abasParaExibir = ABAS_CONTEUDO_ANALISE.filter((aba) => abasHabilitadas.has(aba));
  // Nenhuma habilitada: a aba decide a frase.
  if (abasParaExibir.length === 0) return null;
  // Rótulo desconhecido cai na própria chave, como o `|| aba` do legado.
  return `Considerando: ${abasParaExibir.map((aba) => PARAM_ABAS_LABEL[aba] || aba).join(', ')}`;
}
